/**
 * CrmModal — the shared centered modal shell for CRM workspace dialogs (lead detail, log
 * interaction, etc.). Backdrop + Esc close; panel is white, rounded, scrollable. Sits above the
 * workspace (z 600) and below nothing else it needs to. Design-system styling (DM Sans / Montserrat).
 */
import { useEffect } from 'react'

export default function CrmModal({ title, onClose, children, footer, width = 560 }) {
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose && onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div style={S.backdrop} onMouseDown={e => { if (e.target === e.currentTarget) onClose && onClose() }}>
      <div style={{ ...S.panel, maxWidth: width }}>
        <header style={S.header}>
          <span style={S.title}>{title}</span>
          <button style={S.close} onClick={onClose} title="Close" aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
              <line x1="3" y1="3" x2="13" y2="13" strokeLinecap="round"/>
              <line x1="13" y1="3" x2="3" y2="13" strokeLinecap="round"/>
            </svg>
          </button>
        </header>
        <div style={S.body}>{children}</div>
        {footer && <footer style={S.footer}>{footer}</footer>}
      </div>
    </div>
  )
}

const S = {
  backdrop: {
    position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '6vh 16px 16px',
  },
  panel: {
    width: '100%', maxHeight: '88vh', background: 'var(--aj-bg-elevated)', borderRadius: 10,
    boxShadow: '0 12px 40px rgba(0,0,0,0.28)', display: 'flex', flexDirection: 'column', overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    padding: '14px 18px', borderBottom: '0.5px solid var(--aj-border)', flexShrink: 0,
  },
  title: {
    fontFamily: '"Montserrat", sans-serif', fontWeight: 600, fontSize: 12.5,
    letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)',
  },
  close: { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', padding: 4, display: 'flex', alignItems: 'center' },
  body: { padding: '16px 18px', overflowY: 'auto', flex: 1 },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 18px', borderTop: '0.5px solid var(--aj-border)', flexShrink: 0 },
}

/* Shared control styles reused by CRM modals/forms. */
export const F = {
  label: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, fontWeight: 600, color: 'var(--aj-text-mid)', marginBottom: 4, display: 'block', letterSpacing: '0.02em' },
  input: { width: '100%', fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text)', padding: '7px 9px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'var(--aj-bg-elevated)', boxSizing: 'border-box' },
  textarea: { width: '100%', fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text)', padding: '7px 9px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'var(--aj-bg-elevated)', boxSizing: 'border-box', minHeight: 72, resize: 'vertical' },
  row: { marginBottom: 12 },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  btnPrimary: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, fontWeight: 600, color: '#fff', background: '#07272D', border: 'none', borderRadius: 5, padding: '8px 16px', cursor: 'pointer' },
  btnGhost: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text)', background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 5, padding: '8px 16px', cursor: 'pointer' },
}
